import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

const templateSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  category: z.enum(['worship', 'study', 'event', 'special', 'recurring']),
  titleFormat: z.string().min(1, "Title format is required"),
  defaultBlurb: z.string().optional(),
  defaultTime: z.string().optional(),
  defaultDuration: z.number().optional(),
  isRecurring: z.boolean(),
  recurrencePattern: z.string().optional(),
  recurrenceDayOfWeek: z.number().optional(),
  defaultPlatforms: z.array(z.string()).optional(),
  defaultTags: z.string().optional(),
});

type TemplateForm = z.infer<typeof templateSchema>;

interface TemplateEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: any; // Existing template to edit
}

const daysOfWeek = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const platforms = [
  { id: 'youtube', name: 'YouTube' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'twitch', name: 'Twitch' },
  { id: 'x', name: 'X (Twitter)' },
];

export function TemplateEditorModal({ isOpen, onClose, template }: TemplateEditorModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<TemplateForm>({
    resolver: zodResolver(templateSchema),
    defaultValues: template || {
      name: '',
      description: '',
      category: 'worship',
      titleFormat: '{date} ',
      defaultBlurb: '',
      defaultTime: '10:00',
      defaultDuration: 90,
      isRecurring: false,
      recurrencePattern: 'weekly',
      recurrenceDayOfWeek: 0,
      defaultPlatforms: ['youtube'],
      defaultTags: '',
    },
  });

  const isRecurring = form.watch('isRecurring');

  useEffect(() => {
    if (template) {
      form.reset(template);
    }
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: async (data: TemplateForm) => {
      const url = template
        ? `/api/stream-templates/${template.id}`
        : '/api/stream-templates';
      const method = template ? 'PUT' : 'POST';
      
      const response = await apiRequest(method, url, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/stream-templates'] });
      toast({
        title: template ? 'Template updated!' : 'Template created!',
        description: 'Your stream template has been saved successfully.',
      });
      onClose();
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to save template. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: TemplateForm) => {
    saveMutation.mutate(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'Create New Template'}</DialogTitle>
          <DialogDescription>
            Create a reusable template for quick stream scheduling
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold">Basic Information</h4>
              
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Sunday Morning Service" />
                    </FormControl>
                    <FormDescription>
                      A descriptive name for this template
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} placeholder="Regular Sunday morning worship service" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="worship">Worship</SelectItem>
                        <SelectItem value="study">Study</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                        <SelectItem value="special">Special</SelectItem>
                        <SelectItem value="recurring">Recurring</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Stream Settings */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold">Default Stream Settings</h4>

              <FormField
                control={form.control}
                name="titleFormat"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title Format *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="{date} Sunday Morning Service" />
                    </FormControl>
                    <FormDescription>
                      Use {'{date}'} to insert the stream date automatically
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="defaultBlurb"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} placeholder="Join us for worship, teaching, and fellowship..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="defaultTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Time</FormLabel>
                      <FormControl>
                        <Input {...field} type="time" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="defaultDuration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (minutes)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="defaultTags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Tags</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="worship, church, service" />
                    </FormControl>
                    <FormDescription>
                      Comma-separated tags for this stream type
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Recurrence Settings */}
            <div className="space-y-4 border-t pt-4">
              <FormField
                control={form.control}
                name="isRecurring"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        This is a recurring stream
                      </FormLabel>
                      <FormDescription>
                        Enable if this stream repeats on a regular schedule
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {isRecurring && (
                <div className="grid grid-cols-2 gap-4 ml-7">
                  <FormField
                    control={form.control}
                    name="recurrencePattern"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Frequency</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select frequency..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Bi-weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="recurrenceDayOfWeek"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Day of Week</FormLabel>
                        <Select 
                          onValueChange={(val) => field.onChange(parseInt(val))} 
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select day..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {daysOfWeek.map((day) => (
                              <SelectItem key={day.value} value={day.value.toString()}>
                                {day.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Default Platforms */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold">Default Platforms</h4>
              <FormField
                control={form.control}
                name="defaultPlatforms"
                render={() => (
                  <FormItem>
                    <div className="space-y-2">
                      {platforms.map((platform) => (
                        <FormField
                          key={platform.id}
                          control={form.control}
                          name="defaultPlatforms"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={platform.id}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(platform.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...(field.value || []), platform.id])
                                        : field.onChange(
                                            field.value?.filter((value) => value !== platform.id)
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {platform.name}
                                </FormLabel>
                              </FormItem>
                            )
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveMutation.isPending}
                className="btn-primary"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  template ? 'Update Template' : 'Create Template'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
